/**
 * @author      sammy.dowds@zumper.com (Sammy Dowds)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { Button } from '@zcode-ui/controls'
import { cn } from '@zcode-ui/shadz-ui'
import { VerifiedIcon } from '@zcode-ui/web-zumper-ui'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import React, { Fragment, type MouseEventHandler } from 'react'
import {
  ListingCardCheckboxOrCloseButton,
  type ListingCardCheckboxOrCloseButtonProps,
} from './ListingCardCheckboxOrCloseButton'
import type {
  GetCommonEntityDataInput,
  GetCommonEntityDataOutput,
} from './ListingCardContentSection'
import type { CarouselWrapperProps } from './ListingCardImageSection'
import css from './ListingCardSm.module.scss'
import type { useListingCardStyles } from './useListingCardStyles'

export interface BaseListingCardSmProps {
  Carousel: React.FunctionComponent<CarouselWrapperProps>
  cardBorder: ReturnType<typeof useListingCardStyles>['cardBorder']
  entity: Listable | Pin<RawPinInventorySearch>
  getCommonEntityData: (input: GetCommonEntityDataInput) => GetCommonEntityDataOutput
  getHoodPropertyText: (entity: Listable | Pin<RawPinInventorySearch>) => string
  isShortTerm: boolean | undefined
  ListCardRating: React.ComponentType<{
    gapStyle?: string
    rating?: number | null
    ratingStyle?: string
    textStyle?: string
  }>
  onClick: MouseEventHandler<HTMLDivElement>
  onDoubleClick: MouseEventHandler<HTMLDivElement>
  showBorder: boolean
  showBoxShadow: boolean
}

export type ListingCardSmProps = BaseListingCardSmProps & ListingCardCheckboxOrCloseButtonProps

export const ListingCardSM = ({
  Carousel,
  cardBorder,
  entity,
  getCommonEntityData,
  getHoodPropertyText,
  isShortTerm,
  ListCardRating,
  onDoubleClick,
  showBorder,
  showBoxShadow,
  ...rest
}: ListingCardSmProps) => {
  const {
    features,
    groupId,
    hasImage,
    headerText,
    images,
    isBuilding,
    isVerified,
    maxAdults,
    maxPrice,
    minPrice,
    previousPrice,
    rating,
  } = entity
  const showRating = !!(rating && isBuilding && !isShortTerm) || !!(rating && isShortTerm)

  const {
    bedsRange,
    cityAndHood,
    fullAddress,
    previousPriceText,
    price,
    shortTermHeaderText,
    shortTermPrice,
    shortTermPropertyText,
    showPricePerNight,
  } = getCommonEntityData({
    entity,
    showShortPrice: true,
  })
  const priceDropEnabled = features?.includes(77)
  const showPriceDropAddress =
    priceDropEnabled &&
    !!previousPrice &&
    !!previousPriceText &&
    previousPrice !== minPrice &&
    previousPrice !== maxPrice &&
    maxPrice === minPrice
  const showPriceDropBuilding =
    priceDropEnabled &&
    !!previousPrice &&
    !!previousPriceText &&
    previousPrice !== minPrice &&
    previousPrice !== maxPrice
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div
      onClick={rest.onClick}
      onDoubleClick={onDoubleClick}
      className={cn(
        'flex bg-gray-0 rounded-lg font-body max-w-full overflow-hidden',
        showBorder && rest.isChecked === false && 'm-px',
        showBoxShadow && 'shadow-[0px_8px_16px_rgba(0,0,0,0.12)]'
      )}
    >
      <div
        className={cn(
          'overflow-hidden relative',
          showBorder ? 'h-[114px] w-[115px]' : 'h-[116px] w-[116px]'
        )}
      >
        <Carousel
          customImageHeight={showBorder ? 114 : 116}
          customImageWidth={showBorder ? 115 : 116}
          entity={entity}
          groupId={groupId}
          hasImage={hasImage}
          images={images?.slice(0, 1) ?? null}
          shouldPreloadImage
        />
        <ListingCardCheckboxOrCloseButton {...rest} />
      </div>
      <div className="relative flex flex-1 flex-col justify-between overflow-hidden p-2">
        <div className="flex flex-col whitespace-nowrap">
          <div className="mb-1 flex items-center overflow-hidden whitespace-nowrap">
            {showRating ? (
              <ListCardRating
                gapStyle={css.gapStyle}
                rating={rating}
                ratingStyle={css.ratingStyle}
                textStyle={css.textStyle}
              />
            ) : null}
            {isVerified && (
              <Fragment>
                {showRating && (
                  <div className="border-l-[1.5px] border-solid border-gray-150 h-[13px] ml-1.5 mr-1 " />
                )}
                <VerifiedIcon isDirectDeal isNewDetailPage size={18} />
              </Fragment>
            )}
            {isShortTerm && (
              <Fragment>
                {(showRating || isVerified) && (
                  <div className="border-l-[1.5px] border-solid border-gray-150 h-[13px] ml-1.5 mr-1.5" />
                )}
                <Button variant="link" className="text-sm font-medium text-gray-600 underline">
                  Details
                </Button>
              </Fragment>
            )}
            {!showRating && !isVerified && !isShortTerm && (
              <p className="text-gray-800 text-sm truncate body16Md">
                {getHoodPropertyText(entity)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-px">
            <p className="text-sm truncate display20To24">
              {isShortTerm ? shortTermHeaderText : isBuilding ? headerText : fullAddress}
            </p>
          </div>
          <p className="text-gray-800 text-sm truncate body16Md">
            {isShortTerm ? shortTermPropertyText : isBuilding ? fullAddress : cityAndHood}
          </p>
        </div>
        <div className="flex flex-col">
          <div className="flex items-center justify-between gap-[5px] whitespace-nowrap font-medium leading-5">
            <p className="text-sm text-gray-800 body16Md">{bedsRange}</p>
            <div className="flex items-center">
              {showPriceDropBuilding && !showPriceDropAddress && (
                <p className="text-sm text-red-500 display20To24">
                  {isShortTerm ? shortTermPrice : price}
                </p>
              )}
              {showPriceDropAddress && (
                <Fragment>
                  <p className="text-sm font-normal text-gray-600 line-through display20To24">
                    {previousPriceText}
                  </p>
                  <p className="ml-2 text-sm text-red-500 display20To24">
                    {isShortTerm ? shortTermPrice : price}
                  </p>
                </Fragment>
              )}
              {!showPriceDropBuilding && !showPriceDropAddress && (
                <p className="text-sm display20To24">{isShortTerm ? shortTermPrice : price}</p>
              )}
              {isShortTerm && !showPriceDropBuilding && !showPriceDropAddress && (
                <p className="text-sm text-gray-950">{showPricePerNight ? '/night' : '/month'}</p>
              )}
            </div>
            {isShortTerm && maxAdults && (
              <Fragment>
                <div className="border-l-[1.5px] border-solid border-gray-150 h-[13px]" />
                <p className="truncate text-sm text-gray-800 body16Md">Sleeps {maxAdults}</p>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
